<template>
  <v-container>
    <v-card>
      <div class="d-flex justify-space-between align-center pa-4">
        <h1 class="text-h5">{{ $t('farm_upload_validation.Farm_Upload_Validation', 'Farm Upload Validation') }}</h1>
        <v-btn color="secondary" @click="goBack" text>
          {{ $t('farm_upload_validation.Go_Back', 'Go Back') }}
        </v-btn>
      </div>

      <v-card-text>
        <v-form ref="farmForm">
          <v-simple-table>
            <thead>
              <tr>
                <th width="35%">Input Field</th>
                <th width="40%">Description</th>
                <th width="25%">Validation / Warning Message</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <v-select 
                    v-model="formData.isNotFarmer" 
                    :items="options.isNotFarmerValues"
                    label="I am not a farmer" 
                    outlined
                    @change="validateField('isNotFarmer')"
                    @blur="validateField('isNotFarmer')"
                  ></v-select>
                </td>
                <td>True if you are uploading farms on behalf of farmers.</td>
                <td>
                  <v-alert v-if="validationMessages.isNotFarmer" dense :type="validationMessages.isNotFarmer.type" class="mt-2 text-caption">
                    {{ validationMessages.isNotFarmer.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmerUserId" :rules="rules.farmerUserId"
                    label="User ID of Farmer" outlined @blur="validateField('farmerUserId')"></v-text-field>
                </td>
                <td>Internal user ID of the farmer. Use this to assign the farm to an existing user in the system.</td>
                <td>
                  <v-alert v-if="validationMessages.farmerUserId" dense :type="validationMessages.farmerUserId.type" class="mt-2 text-caption">
                    {{ validationMessages.farmerUserId.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmerRegistrationId" :rules="rules.farmerRegistrationId"
                    label="Farmer Registration ID" outlined @blur="validateField('farmerRegistrationId')"></v-text-field>
                </td>
                <td>A unique identifier for the farmer. Assigned by a governmental or agricultural body.</td>
                <td>
                  <v-alert v-if="validationMessages.farmerRegistrationId" dense :type="validationMessages.farmerRegistrationId.type" class="mt-2 text-caption">
                    {{ validationMessages.farmerRegistrationId.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmerInternalId" :rules="rules.farmerInternalId"
                    label="Farmer ID" outlined @blur="validateField('farmerInternalId')"></v-text-field>
                </td>
                <td>A unique identifier for the farmer used internally.</td>
                <td>
                  <v-alert v-if="validationMessages.farmerInternalId" dense :type="validationMessages.farmerInternalId.type" class="mt-2 text-caption">
                    {{ validationMessages.farmerInternalId.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmerFirstName" :rules="rules.farmerFirstName"
                    label="Farmer First Name" outlined @blur="validateField('farmerFirstName')"></v-text-field>
                </td>
                <td>The legal first name of the farmer.</td>
                <td>
                  <v-alert v-if="validationMessages.farmerFirstName" dense :type="validationMessages.farmerFirstName.type" class="mt-2 text-caption">
                    {{ validationMessages.farmerFirstName.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmerMiddleName" :rules="rules.farmerMiddleName"
                    label="Farmer Middle Name" outlined @blur="validateField('farmerMiddleName')"></v-text-field>
                </td>
                <td>The legal middle name of the farmer, if applicable.</td>
                <td>
                  <v-alert v-if="validationMessages.farmerMiddleName" dense :type="validationMessages.farmerMiddleName.type" class="mt-2 text-caption">
                    {{ validationMessages.farmerMiddleName.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmerLastName" :rules="rules.farmerLastName"
                    label="Farmer Last Name" outlined @blur="validateField('farmerLastName')"></v-text-field>
                </td>
                <td>The legal last name of the farmer.</td>
                <td>
                  <v-alert v-if="validationMessages.farmerLastName" dense :type="validationMessages.farmerLastName.type" class="mt-2 text-caption">
                    {{ validationMessages.farmerLastName.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmInternalId" :rules="rules.farmInternalId"
                    label="Farm ID" outlined @blur="validateField('farmInternalId')"></v-text-field>
                </td>
                <td>A unique identifier for the farm used internally.</td>
                <td>
                  <v-alert v-if="validationMessages.farmInternalId" dense :type="validationMessages.farmInternalId.type" class="mt-2 text-caption">
                    {{ validationMessages.farmInternalId.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmName" :rules="rules.farmName"
                    label="Farm Name" outlined @blur="validateField('farmName')"></v-text-field>
                </td>
                <td>The registered or commonly used name of the farm. If provided, requires backend uniqueness validation.</td>
                <td>
                  <v-alert v-if="validationMessages.farmName" dense :type="validationMessages.farmName.type" class="mt-2 text-caption">
                    {{ validationMessages.farmName.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmRegistrationId" :rules="rules.farmRegistrationId"
                    label="Farm Registration ID" outlined @blur="validateField('farmRegistrationId')"></v-text-field>
                </td>
                <td>A unique identifier for the farm. Assigned by a governmental or agricultural body.</td>
                <td>
                  <v-alert v-if="validationMessages.farmRegistrationId" dense :type="validationMessages.farmRegistrationId.type" class="mt-2 text-caption">
                    {{ validationMessages.farmRegistrationId.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.nationalFarmId" :rules="rules.nationalFarmId"
                    label="National Farm ID" outlined @blur="validateField('nationalFarmId')"></v-text-field>
                </td>
                <td>A unique identifier for the farm. Assigned by a national body.</td>
                <td>
                  <v-alert v-if="validationMessages.nationalFarmId" dense :type="validationMessages.nationalFarmId.type" class="mt-2 text-caption">
                    {{ validationMessages.nationalFarmId.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-select v-model="formData.ownershipType" :items="options.ownership" :rules="rules.ownershipType"
                    label="Ownership Type *" outlined @change="validateField('ownershipType')" @blur="validateField('ownershipType')"></v-select>
                </td>
                <td>Categorize the legal ownership structure of the farm (e.g., Individual, Corporate, Cooperative).</td>
                <td>
                  <v-alert v-if="validationMessages.ownershipType" dense :type="validationMessages.ownershipType.type" class="mt-2 text-caption">
                    {{ validationMessages.ownershipType.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-select v-model="formData.productionType" :items="options.production" :rules="rules.productionType"
                    label="Production Type *" outlined @change="validateField('productionType')" @blur="validateField('productionType')"></v-select>
                </td>
                <td>Specify the primary production method used on the farm (e.g., Organic, Conventional, Mixed).</td>
                <td>
                  <v-alert v-if="validationMessages.productionType" dense :type="validationMessages.productionType.type" class="mt-2 text-caption">
                    {{ validationMessages.productionType.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.country" :rules="rules.country"
                    label="Country" outlined @blur="validateField('country')"></v-text-field>
                </td>
                <td>The country where the farm is physically located.</td>
                <td>
                  <v-alert v-if="validationMessages.country" dense :type="validationMessages.country.type" class="mt-2 text-caption">
                    {{ validationMessages.country.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.provinceState" :rules="rules.provinceState"
                    label="Province/State" outlined @blur="validateField('provinceState')"></v-text-field>
                </td>
                <td>The state, province, or primary administrative region of the farm.</td>
                <td>
                  <v-alert v-if="validationMessages.provinceState" dense :type="validationMessages.provinceState.type" class="mt-2 text-caption">
                    {{ validationMessages.provinceState.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.villageTownCity" :rules="rules.villageTownCity"
                    label="Village/Town/City" outlined @blur="validateField('villageTownCity')"></v-text-field>
                </td>
                <td>The local municipality, town, or village where the farm is located.</td>
                <td>
                  <v-alert v-if="validationMessages.villageTownCity" dense :type="validationMessages.villageTownCity.type" class="mt-2 text-caption">
                    {{ validationMessages.villageTownCity.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.streetNumber" :rules="rules.streetNumber"
                    label="Street Number" outlined @blur="validateField('streetNumber')"></v-text-field>
                </td>
                <td>The specific street address or plot number of the farm.</td>
                <td>
                  <v-alert v-if="validationMessages.streetNumber" dense :type="validationMessages.streetNumber.type" class="mt-2 text-caption">
                    {{ validationMessages.streetNumber.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.latitude" :rules="rules.latitude" type="number"
                    label="Latitude" outlined @blur="validateField('latitude')"></v-text-field>
                </td>
                <td>The exact GPS latitude coordinate of the farm's primary location.</td>
                <td>
                  <v-alert v-if="validationMessages.latitude" dense :type="validationMessages.latitude.type" class="mt-2 text-caption">
                    {{ validationMessages.latitude.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.longitude" :rules="rules.longitude" type="number"
                    label="Longitude" outlined @blur="validateField('longitude')"></v-text-field>
                </td>
                <td>The exact GPS longitude coordinate of the farm's primary location.</td>
                <td>
                  <v-alert v-if="validationMessages.longitude" dense :type="validationMessages.longitude.type" class="mt-2 text-caption">
                    {{ validationMessages.longitude.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-textarea v-model="formData.geofenceCoordinates" :rules="rules.geofenceCoordinates"
                    label="Geofence Coordinates" outlined @blur="validateField('geofenceCoordinates')" rows="2"></v-textarea>
                </td>
                <td>For polygon areas, provide the boundary coordinate pairs (latitude, longitude) that outline the farm.</td>
                <td>
                  <v-alert v-if="validationMessages.geofenceCoordinates" dense :type="validationMessages.geofenceCoordinates.type" class="mt-2 text-caption">
                    {{ validationMessages.geofenceCoordinates.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.geofenceName" :rules="rules.geofenceName"
                    label="Geofence Name" outlined @blur="validateField('geofenceName')"></v-text-field>
                </td>
                <td>A descriptive name for this specific geofenced area (e.g., 'Main Pasture').</td>
                <td>
                  <v-alert v-if="validationMessages.geofenceName" dense :type="validationMessages.geofenceName.type" class="mt-2 text-caption">
                    {{ validationMessages.geofenceName.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.geofenceCategory" :rules="rules.geofenceCategory"
                    label="Geofence Category" outlined @blur="validateField('geofenceCategory')"></v-text-field>
                </td>
                <td>The category of the geofence (e.g., Field, Storage, Processing).</td>
                <td>
                  <v-alert v-if="validationMessages.geofenceCategory" dense :type="validationMessages.geofenceCategory.type" class="mt-2 text-caption">
                    {{ validationMessages.geofenceCategory.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-select v-model="formData.geofenceType" :items="options.geofenceTypes" :rules="rules.geofenceType"
                    label="Geofence Type" outlined @change="validateField('geofenceType')" @blur="validateField('geofenceType')"></v-select>
                </td>
                <td>The geometric shape of the geofence (e.g., Polygon, Circle).</td>
                <td>
                  <v-alert v-if="validationMessages.geofenceType" dense :type="validationMessages.geofenceType.type" class="mt-2 text-caption">
                    {{ validationMessages.geofenceType.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.geofenceCenterLatitude" :rules="rules.geofenceCenterLatitude" type="number"
                    label="Geofence Center Latitude" outlined @blur="validateField('geofenceCenterLatitude')"></v-text-field>
                </td>
                <td>The central latitude coordinate of the geofenced area.</td>
                <td>
                  <v-alert v-if="validationMessages.geofenceCenterLatitude" dense :type="validationMessages.geofenceCenterLatitude.type" class="mt-2 text-caption">
                    {{ validationMessages.geofenceCenterLatitude.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.geofenceCenterLongitude" :rules="rules.geofenceCenterLongitude" type="number"
                    label="Geofence Center Longitude" outlined @blur="validateField('geofenceCenterLongitude')"></v-text-field>
                </td>
                <td>The central longitude coordinate of the geofenced area.</td>
                <td>
                  <v-alert v-if="validationMessages.geofenceCenterLongitude" dense :type="validationMessages.geofenceCenterLongitude.type" class="mt-2 text-caption">
                    {{ validationMessages.geofenceCenterLongitude.text }}
                  </v-alert>
                </td>
              </tr>

              <tr>
                <td>
                  <v-text-field v-model="formData.farmAreaHectares" :rules="rules.farmAreaHectares" type="number"
                    label="Farm Area in Hectares" outlined @blur="validateField('farmAreaHectares')"></v-text-field>
                </td>
                <td>The total workable area of the farm measured in hectares.</td>
                <td>
                  <v-alert v-if="validationMessages.farmAreaHectares" dense :type="validationMessages.farmAreaHectares.type" class="mt-2 text-caption">
                    {{ validationMessages.farmAreaHectares.text }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-form>
      </v-card-text>
      
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="success" x-large @click="submitForm">Submit Farm Profile</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      formData: {
        isNotFarmer: null,
        farmerUserId: '',
        farmerRegistrationId: '',
        farmerInternalId: '',
        farmerFirstName: '',
        farmerMiddleName: '',
        farmerLastName: '',
        farmInternalId: '',
        farmName: '',
        farmRegistrationId: '',
        nationalFarmId: '',
        ownershipType: null,
        productionType: null,
        country: '',
        provinceState: '',
        villageTownCity: '',
        streetNumber: '',
        latitude: '',
        longitude: '',
        geofenceCoordinates: '',
        geofenceName: '',
        geofenceCategory: '',
        geofenceType: null,
        geofenceCenterLatitude: '',
        geofenceCenterLongitude: '',
        farmAreaHectares: '',
      },
      options: {
        isNotFarmerValues: [true, false, 1, 0, 'yes', 'no'],
        ownership: ["Individual", "Partnership", "Corporate", "Cooperative"],
        production: ["Organic", "Conventional", "Mixed"],
        geofenceTypes: ["Polygon", "Circle"]
      },
      // validationMessages will store objects like: { text: "Warning string", type: "warning" | "error" }
      validationMessages: {},
      
      // We store warnings here to keep the rules object strictly for format/hard-errors
      warnings: {
        isNotFarmer: "Optional: Please clarify if you are uploading this on behalf of someone else.",
        farmerUserId: "Optional: If left blank, this farm will not be associated with a specific user profile in the system.",
        farmerRegistrationId: "Optional: Omitting this means official farmer registry details won't be linked.",
        farmerInternalId: "Optional: Internal tracking metrics for this farmer will be bypassed.",
        farmerFirstName: "Optional: The farmer's first name won't appear on printable reports.",
        farmerMiddleName: "Optional: Middle name will be excluded from records.",
        farmerLastName: "Optional: The farmer's last name won't appear on printable reports.",
        farmInternalId: "Optional: Internal tracking IDs for this farm will be bypassed.",
        farmName: "Optional: Farm will be listed without a name, making it harder to search in directories.",
        farmRegistrationId: "Optional: Omitting this means official farm registry details won't be linked.",
        nationalFarmId: "Optional: National compliance tracking IDs won't be linked to this farm.",
        country: "Optional: Missing country data means this farm won't appear in national-level data filters.",
        provinceState: "Optional: Missing state/province data means this farm won't appear in regional map filters.",
        villageTownCity: "Optional: Missing town data restricts local-level searches and weather API mapping.",
        streetNumber: "Optional: Exact street addressing will not be available for logistics.",
        latitude: "Optional: Without latitude, the farm cannot be plotted as a pin on any system maps.",
        longitude: "Optional: Without longitude, the farm cannot be plotted as a pin on any system maps.",
        geofenceCoordinates: "Optional: Without these coordinates, the user won't be able to see the physical polygon outline on the map.",
        geofenceName: "Optional: Map layers will display this geofence as 'Unnamed Area'.",
        geofenceCategory: "Optional: You won't be able to color-code or filter map layers by category (e.g., fields vs. storage).",
        geofenceType: "Optional: The system may default to basic point rendering instead of shapes.",
        geofenceCenterLatitude: "Optional: 'Snap to Center' map functions will be disabled for this farm.",
        geofenceCenterLongitude: "Optional: 'Snap to Center' map functions will be disabled for this farm.",
        farmAreaHectares: "Optional: Yield-per-hectare calculations and size-based analytics will be completely unavailable."
      },

      rules: {
        // STRICTLY REQUIRED FIELDS
        ownershipType: [
          v => !!v || 'Ownership Type is strictly required to categorize the farm.',
        ],
        productionType: [
          v => !!v || 'Production Type is strictly required to categorize the farm.',
        ],
        
        // FORMAT VALIDATION ONLY (Passes if empty)
        latitude: [
          v => !v || !isNaN(parseFloat(v)) || 'If provided, Latitude must be a valid number.',
        ],
        longitude: [
          v => !v || !isNaN(parseFloat(v)) || 'If provided, Longitude must be a valid number.',
        ],
        geofenceCenterLatitude: [
          v => !v || !isNaN(parseFloat(v)) || 'If provided, Latitude must be a valid number.',
        ],
        geofenceCenterLongitude: [
          v => !v || !isNaN(parseFloat(v)) || 'If provided, Longitude must be a valid number.',
        ],
        farmAreaHectares: [
          v => !v || (!isNaN(parseFloat(v)) && parseFloat(v) > 0) || 'If provided, Farm Area must be a positive number.',
        ]
      }
    }
  },
  mounted() {
    // Optionally trigger warnings on load for empty fields so users know immediately
    Object.keys(this.formData).forEach(field => {
      if(field !== 'ownershipType' && field !== 'productionType') {
        this.validateField(field);
      }
    });
  },
  methods: {
    async validateField(field) {
      // 1. Clear previous state
      this.$set(this.validationMessages, field, null);
      
      const value = this.formData[field];
      const rules = this.rules[field] || [];

      // 2. Run strictly defined Vuetify rules (These block submission)
      for (const rule of rules) {
        const result = rule(value);
        if (result !== true) {
          // Set as an ERROR
          this.$set(this.validationMessages, field, { text: result, type: 'error' });
          return false;
        }
      }

      // 3. Specialized Async Check for Farm Name (Only runs if a name is actually typed)
      if (field === 'farmName' && value) {
        const isUnique = await this.checkFarmName(value);
        if (!isUnique) {
          this.$set(this.validationMessages, field, { text: 'This Farm Name already exists in the system.', type: 'error' });
          return false;
        }
      }

      // 4. If rules pass and value is empty (or explicitly null for dropdowns), check if we need to show a Warning
      if ((value === '' || value === null || value === undefined) && this.warnings[field]) {
        this.$set(this.validationMessages, field, { text: this.warnings[field], type: 'warning' });
        return true; // Returns true because warnings do NOT block submission
      }

      return true; // Field is valid (or filled properly so warning is cleared)
    },

    async checkFarmName(farmName) {
      try {
        const response = await axios.get(`/farm/farm-name-validation`, { params: { farmName } });
        return !!response.data?.success; 
      } catch (error) {
        console.error('Error checking farm name:', error);
        return false;
      }
    },

    async submitForm() {
      // Run Vuetify's internal validate (checks the :rules array)
      const isFormValid = this.$refs.farmForm.validate();
      
      // Ensure we explicitly trigger validation on all fields to catch async errors
      const fields = Object.keys(this.formData);
      await Promise.all(fields.map(f => this.validateField(f)));
      
      // Check if any active validation message is strictly an 'error'
      const hasErrors = Object.values(this.validationMessages).some(msg => msg && msg.type === 'error');
      
      if (isFormValid && !hasErrors) {
        alert('Form submitted successfully! (Warnings ignored)');
        // INSERT API POST LOGIC HERE
      } else {
        console.error("Form blocked: Please fix the errors highlighted in red.");
      }
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep td {
  padding: 12px 16px !important;
  height: auto !important;
  vertical-align: top;
}

.v-alert {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.2;
}

/* Slightly dim warning alerts so they aren't as aggressive as errors */
.v-alert.warning {
  opacity: 0.9;
}
</style>